import { API_BASE_URL, handleResponse, getAuthHeaders } from '@/lib/api-client';

export type AssignableType = 'story' | 'story_theme' | 'flashcard_deck' | 'quiz' | 'fluency_passage';
export type AssignmentSource = 'parent' | 'system' | 'teacher';
export type AssignmentStatus = 'pending' | 'in_progress' | 'completed' | 'overdue' | 'archived';

export interface Assignment {
  id: string;
  assigner_id: string;
  assignee_id: string;
  title: string;
  instructions: string | null;
  assignable_type: AssignableType;
  assignable_id: string;
  status: AssignmentStatus;
  assignment_source: AssignmentSource;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  assignment_metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export interface AssignmentWithDetails extends Assignment {
  assignee_name: string | null;
  assignee_display_name: string | null;
  assigner_name: string | null;

  // Resource-specific details (populated based on assignable_type)
  story_title: string | null;
  story_theme: string | null;
  deck_title: string | null;
  deck_card_count: number | null;
  quiz_title: string | null;
  quiz_question_count: number | null;
}

export interface AssignmentCreate {
  assignee_id: string;
  title: string;
  instructions?: string;
  assignable_type: AssignableType;
  assignable_id: string;
  due_date?: string;
  assignment_metadata?: Record<string, any>;
}

export interface AssignmentUpdate {
  title?: string;
  instructions?: string;
  status?: AssignmentStatus;
  due_date?: string;
  metadata?: Record<string, any>;
}

export const assignmentsApi = {
  async getAssignments(includeCompleted: boolean = false, childId?: string): Promise<AssignmentWithDetails[]> {
    let url = `${API_BASE_URL}/assignments?include_completed=${includeCompleted}`;
    if (childId) url += `&child_id=${childId}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<AssignmentWithDetails[]>(response, 'Failed to fetch assignments');
  },

  async getAssignment(assignmentId: string): Promise<AssignmentWithDetails> {
    const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<AssignmentWithDetails>(response, 'Failed to fetch assignment');
  },

  async createAssignment(data: AssignmentCreate): Promise<Assignment> {
    const response = await fetch(`${API_BASE_URL}/assignments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Assignment>(response, 'Failed to create assignment');
  },

  async completeAssignment(assignmentId: string, sessionId: string): Promise<Assignment> {
    const response = await fetch(
      `${API_BASE_URL}/assignments/${assignmentId}/complete?session_id=${sessionId}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<Assignment>(response, 'Failed to complete assignment');
  },

  async updateAssignment(assignmentId: string, data: AssignmentUpdate): Promise<Assignment> {
    const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<Assignment>(response, 'Failed to update assignment');
  },

  async deleteAssignment(assignmentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<void>(response, 'Failed to delete assignment');
  },

  async getPendingAssignmentsCount(): Promise<{ pending_count: number }> {
    const response = await fetch(`${API_BASE_URL}/assignments/count/pending`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ pending_count: number }>(
      response,
      'Failed to fetch pending assignments count'
    );
  },

  async getRecentlyCompletedAssignments(): Promise<AssignmentWithDetails[]> {
    const response = await fetch(
      `${API_BASE_URL}/assignments/recently-completed`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<AssignmentWithDetails[]>(
      response,
      'Failed to fetch recently completed assignments'
    );
  },

  async recommendAssignments(childId: string): Promise<{ job_id: number; message: string }> {
    const response = await fetch(
      `${API_BASE_URL}/assignments/recommend?child_id=${childId}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<{ job_id: number; message: string }>(
      response,
      'Failed to generate smart assignments'
    );
  },
};
